import { apiClient } from './api-client';
import type { AuthUser, LoginResponse } from '@/types/auth.types';

export const authService = {
  /**
   * `secondaryIdentifier` (username or mobile) is sent only after the API has
   * answered 409 ADDITIONAL_IDENTIFICATION_REQUIRED, meaning the email address
   * is shared by more than one active account. Omitted on a first attempt.
   */
  login: (
    identifier: string,
    password: string,
    secondaryIdentifier?: string,
    captcha?: { token: string; answer: string },
  ): Promise<LoginResponse> =>
    apiClient
      .post<LoginResponse>('/auth/login', {
        identifier,
        password,
        ...(secondaryIdentifier ? { secondaryIdentifier } : {}),
        ...(captcha?.token ? { captchaToken: captcha.token, captchaAnswer: captcha.answer } : {}),
      })
      .then((r) => r.data),

  /**
   * D-099 — anonymous captcha challenge for the login form.
   *
   * A separate endpoint from the customer one: the login page has no token and
   * no tenant, so the tenant-scoped `/captcha` cannot serve it.
   */
  publicCaptcha: (): Promise<{ token: string; question: string; expiresInSeconds: number }> =>
    apiClient
      .get<{ token: string; question: string; expiresInSeconds: number }>('/captcha/public')
      .then((r) => r.data),

  refresh: (refreshToken: string): Promise<LoginResponse> =>
    apiClient.post<LoginResponse>('/auth/refresh', { refreshToken }).then((r) => r.data),

  logout: (refreshToken: string): Promise<void> =>
    apiClient.post('/auth/logout', { refreshToken }).then(() => undefined),

  getMe: (): Promise<AuthUser> => apiClient.get<AuthUser>('/auth/me').then((r) => r.data),

  changePassword: (currentPassword: string, newPassword: string): Promise<void> =>
    apiClient
      .patch('/auth/change-password', { currentPassword, newPassword })
      .then(() => undefined),
};
